import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import MapIcon from '@mui/icons-material/Map';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import apiService from '../services/apiService';
import AttendanceMap from './Leave/AttendanceMap';
import './NewDashboard.css';
import LeaveBalanceWidget from './Leave/LeaveBalanceWidget';
import { usePermissions } from './PermissionsContext';
import HelpCard from './Dashboard/HelpCard';
import UpcomingEventsWidget from './Dashboard/UpcomingEventsWidget';
import ShortAbsenceMapWidget from './Dashboard/ShortAbsenceMapWidget';

const NewDashboard = () => {
  const [employees, setEmployees] = useState([]);
  const { role } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    fetchEmployees();
    fetchLeaveStats();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await apiService.getEmployees();
      setEmployees(response?.data ?? []);
    } catch (err) {
      console.warn('API call failed, falling back to empty data:', err?.message || err);
      setEmployees([]); // empty real list when API fails
      setError('');
    } finally {
      setLoading(false);
    }
  };

  const [lrStats, setLrStats] = useState({ pending: 0, thisMonth: 0, approved: 0 });
  const fetchLeaveStats = async () => {
    try {
      const data = await apiService.get('/leave-requests');
      const arr = Array.isArray(data) ? data : [];
      const now = new Date();
      const month = now.getMonth();
      const year = now.getFullYear();
      let pending = 0, thisMonth = 0, approved = 0;
      for (const r of arr) {
        const st = (r.status || r.Status || '').toString();
        if (st === 'Pending') pending++;
        if (st === 'Approved') {
          approved++;
          const sd = new Date(r.startDate || r.StartDate);
          if (!isNaN(sd) && sd.getMonth() === month && sd.getFullYear() === year) thisMonth++;
        }
      }
      setLrStats({ pending, thisMonth, approved });
    } catch (e) {
      setLrStats({ pending: 0, thisMonth: 0, approved: 0 });
    }
  };

  const totalEmployees = useMemo(()=> (employees?.length || 0), [employees]);
  const employeesById = useMemo(() => {
    const map = {};
    (employees || []).forEach(e => { map[e.id || e.Id] = e; });
    return map;
  }, [employees]);

  if (loading) {
    return (
      <Box className="dashboard-loading">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading dashboard...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Roles that should NOT see Leave Balance
  const hideLeaveBalance = ['SystemAdmin', 'BoardManager', 'HR', 'HRManager'].includes(role);

  return (
    <Box className="dashboard-container">
      {/* Tab Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<EventIcon />} iconPosition="start" label="Upcoming Events" />
          <Tab icon={<MapIcon />} iconPosition="start" label="Attendance Map" />
          <Tab icon={<AccessTimeIcon />} iconPosition="start" label="Short Absence" />
          <Tab icon={<HelpOutlineIcon />} iconPosition="start" label="Help & Support" />
        </Tabs>
      </Box>

      {/* Tab 0: Upcoming Events */}
      {activeTab === 0 && (
        <UpcomingEventsWidget days={365} limit={100} />
      )}

      {/* Tab 1: Attendance Map (Leave Requests) */}
      {activeTab === 1 && (
        <Card className="combo-card">
          <CardContent sx={{ p: 2 }}>
            {(() => {
              const now = new Date();
              const from = new Date(now.getFullYear(), now.getMonth(), 1);
              const to = new Date(now.getFullYear(), now.getMonth() + 2, 0);
              return <AttendanceMap from={from} to={to} showYearSelector={false} employeesById={employeesById} />;
            })()}
          </CardContent>
        </Card>
      )}

      {/* Tab 2: Short Absence Map */}
      {activeTab === 2 && (
        <ShortAbsenceMapWidget />
      )}

      {/* Tab 3: Help & Support */}
      {activeTab === 3 && (
        <HelpCard />
      )}
    </Box>
  );
};

export default NewDashboard;
